// WhyNow — one person's next best action, and the reason the rules give for it (§10).
//
// It sits on the Person page above the timeline, and it answers the only question a standing
// brief cannot answer by listing rows: *of everything on this page, what is the move today?*
//
// THREE THINGS IT WILL NOT DO:
//
//  · It will not invent a reason. The sentence comes from the server, composed from the score
//    breakdown by the rules or rewritten by the `why_now` skill. A missing action renders the
//    honest state ("nothing is asking for a move"), never a placeholder sentence.
//  · It will not put ◇ on arithmetic. The score is a computed number and carries no mark. The
//    PROSE carries the mark, and only when an agent wrote it — `why_now_by` other than 'rules'.
//  · It will not tell the operator what the score is made of and hide the parts. The breakdown
//    is one click away, and every line names its rule, its points and its `because`.
import { useCallback, useEffect, useState } from 'react';
import { Att, Fig, Lnk, Record, Sheet, Tag, Zone, ZoneHead } from './PageKit.js';
import { useT } from '../i18n/index.js';
import { useToast } from './Toast.js';
import { ApiError } from '../api/client.js';
import { completeAction, dismissAction, getScore, listActions } from '../api/scoring.js';
import type { Action, ActionKind, Score } from '../../shared/types.js';
import './WhyNow.de.js';
import './WhyNow.fr.js';

export interface WhyNowProps {
  personId: number;
  /** Called after Done or Dismiss, so the page around it can reload what changed. */
  onActed?: () => void;
}

/** The verb's tone. Only what wants the operator NOW is coloured; `wait` never is. */
const KIND_TONE: Record<ActionKind, 'neutral' | 'attend' | 'broken'> = {
  send: 'attend', call: 'attend', meet: 'attend', research: 'neutral', introduce: 'attend', wait: 'neutral',
};

const KIND_LABEL: Record<ActionKind, string> = {
  send: 'send', call: 'call', meet: 'meet', research: 'research', introduce: 'introduce', wait: 'wait',
};

export default function WhyNow({ personId, onActed }: WhyNowProps) {
  const t = useT();
  const toast = useToast();

  const [action, setAction] = useState<Action | null>(null);
  const [score, setScore] = useState<Score | null>(null);
  /** null while the first read is in flight — which is not "there is nothing". */
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openBreakdown, setOpenBreakdown] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [actions, s] = await Promise.all([
        listActions({ person_id: personId, status: 'open', limit: 1 }),
        getScore(personId),
      ]);
      setAction(actions.items[0] ?? null);
      setScore(s);
      setError(null);
    } catch (err) {
      // A failed read is silence: no action, no score, and the zone says it could not read.
      setAction(null);
      setScore(null);
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoaded(true);
    }
  }, [personId]);

  useEffect(() => { void load(); }, [load]);

  const act = useCallback(async (verb: 'done' | 'dismiss') => {
    if (!action) return;
    setBusy(true);
    try {
      if (verb === 'done') await completeAction(action.id);
      else await dismissAction(action.id);
      toast('ok', verb === 'done'
        ? t('Marked done. It will not be proposed again until the data asks for it.')
        : t('Dismissed. It will not be proposed again for seven days.'));
      await load();
      onActed?.();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [action, load, onActed, t, toast]);

  const agentWrote = !!action?.why_now_by && action.why_now_by !== 'rules';

  return (
    <Zone id="why-now">
      <ZoneHead
        label={t('Why now')}
        note={t('one action, chosen by the highest-scoring reason')}
        endNote={score ? <>{t('score')} <Fig>{score.score}</Fig></> : undefined}
      />
      <Sheet pad edge={!!action && action.kind !== 'wait'}>
        {!loaded ? (
          <p className="zone-empty">{t('Reading the score…')}</p>
        ) : error ? (
          <p className="zone-empty">{t('The next best action could not be read: {error}', { error })}</p>
        ) : !score ? (
          <p className="zone-empty">
            {t('Nothing has scored this person yet. The rules run at 05:00, and on every interaction, signal and commitment; until one of those happens there is no score and no reason to show.')}
          </p>
        ) : !action ? (
          <p className="zone-empty">
            {t('Nothing is asking for a move: no open commitment with us, no signal in thirty days, and the cadence is being kept.')}
          </p>
        ) : (
          <>
            <div className="move-head">
              <h2 style={{ fontSize: 'var(--t-h3)' }}>
                {action.title ?? t(KIND_LABEL[action.kind])}{' '}
                <Tag tone={KIND_TONE[action.kind]}>{t(KIND_LABEL[action.kind])}</Tag>
              </h2>
              <div className="figs move-cta">
                <div className="f">
                  <span className="k">{t('Score')}</span>
                  <span className="v">{score.score}</span>
                </div>
              </div>
            </div>

            {action.why_now && (
              <Record className="read" >
                {agentWrote && <><Att kind="proposed" title={t('written by the why_now skill; nobody has held it')} /> </>}
                {action.why_now}
              </Record>
            )}
            <p className="zone-note" style={{ marginTop: 4 }}>
              {agentWrote
                ? t('written by {by} · the rule text stays underneath', { by: action.why_now_by ?? '' })
                : t('composed by the rules from the score breakdown')}
            </p>

            {action.runners_up.length > 0 && (
              <p className="zone-empty" style={{ marginTop: 8 }}>
                {t('Runners-up')}: {action.runners_up.map((r, i) => (
                  <span key={`${r.kind}-${i}`}>
                    {i > 0 && ' · '}
                    <b>{t(KIND_LABEL[r.kind])}</b> — {r.why_now}
                  </span>
                ))}
              </p>
            )}

            <div className="mast-verbs" style={{ marginTop: 12, justifyContent: 'flex-start', gap: 8 }}>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => { void act('done'); }}>
                {t('Done')}
              </button>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => { void act('dismiss'); }}>
                {t('Dismiss')}
              </button>
              <Lnk onClick={() => setOpenBreakdown((v) => !v)}>
                {openBreakdown ? t('Hide the breakdown') : t('Why this score')}
              </Lnk>
            </div>

            {openBreakdown && (
              <div style={{ marginTop: 10 }}>
                {score.breakdown.length ? score.breakdown.map((r, i) => (
                  <div className="row" style={{ gridTemplateColumns: '150px 60px 1fr' }} key={`${r.rule}-${i}`}>
                    <span className="k">{r.rule}</span>
                    <span className="voice-figure" style={{ color: r.points < 0 ? 'var(--attend)' : undefined }}>
                      {r.points > 0 ? `+${r.points}` : r.points}
                    </span>
                    <span className="d">{r.because}</span>
                  </div>
                )) : (
                  <p className="zone-empty">{t('No rule fired. The score is zero because nothing matched, not because nothing was looked at.')}</p>
                )}
              </div>
            )}
          </>
        )}
      </Sheet>
    </Zone>
  );
}
